/**
 * @file FloatingTooltip.tsx
 * @description
 * -----------------------------------------------------
 * Componente genérico de tooltip para reutilizar lógica de apertura y cierre
 * -----------------------------------------------------
 * @version 1.0.0
 * @created 2025-11-25
 * @author Correcta
 * @author Sergio Manjón
 * @copyright © Correcta
 * @license Private
 */

"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { TooltipProps } from "@/types/ui";

const FloatingTooltip = ({
  children,
  content,
  side = "top",
  offset = 8,
  disabled,
  className,
}: TooltipProps & {
  disabled?: boolean;
  offset?: number;
  className?: string;
}) => {
  const targetRef = useRef<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    maxWidth: number;
  } | null>(null);

  const showTooltip = (element: HTMLElement) => {
    if (disabled || !content) return;
    targetRef.current = element;
    setIsOpen(true);
    setPosition(null);
  };

  const hideTooltip = () => {
    setIsOpen(false);
  };

  const updatePosition = useCallback(() => {
    if (!targetRef.current || !tooltipRef.current) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const maxWidth = Math.min(viewportWidth - margin * 2, 320);

    tooltipRef.current.style.maxWidth = `${maxWidth}px`;

    requestAnimationFrame(() => {
      if (!targetRef.current || !tooltipRef.current) return;

      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const targetRect = targetRef.current.getBoundingClientRect();

      const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), max);

      const computePosition = (
        sideValue: "top" | "bottom" | "left" | "right",
      ) =>
        sideValue === "right"
          ? {
              top:
                targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
              left: targetRect.right + offset,
            }
          : sideValue === "left"
            ? {
                top:
                  targetRect.top +
                  targetRect.height / 2 -
                  tooltipRect.height / 2,
                left: targetRect.left - tooltipRect.width - offset,
              }
            : sideValue === "bottom"
              ? {
                  top: targetRect.bottom + offset,
                  left:
                    targetRect.left +
                    targetRect.width / 2 -
                    tooltipRect.width / 2,
                }
              : {
                  top: targetRect.top - tooltipRect.height - offset,
                  left:
                    targetRect.left +
                    targetRect.width / 2 -
                    tooltipRect.width / 2,
                };

      const fits = (sideValue: "top" | "bottom" | "left" | "right") => {
        const candidate = computePosition(sideValue);
        return (
          candidate.left >= margin &&
          candidate.top >= margin &&
          candidate.left + tooltipRect.width <= viewportWidth - margin &&
          candidate.top + tooltipRect.height <= viewportHeight - margin
        );
      };

      const spaces = {
        top: targetRect.top,
        bottom: viewportHeight - targetRect.bottom,
        left: targetRect.left,
        right: viewportWidth - targetRect.right,
      };

      const sides: Array<"top" | "bottom" | "left" | "right"> = [
        "top",
        "bottom",
        "right",
        "left",
      ];

      let chosenSide: "top" | "bottom" | "left" | "right" = side;
      if (!fits(side)) {
        const fittingSide = sides.find((candidate) => fits(candidate));
        if (fittingSide) {
          chosenSide = fittingSide;
        } else {
          chosenSide = sides.reduce((best, candidate) =>
            spaces[candidate] > spaces[best] ? candidate : best,
          );
        }
      }

      const raw = computePosition(chosenSide);
      const top = clamp(
        raw.top,
        margin,
        viewportHeight - tooltipRect.height - margin,
      );
      const left = clamp(
        raw.left,
        margin,
        viewportWidth - tooltipRect.width - margin,
      );

      setPosition({ top, left, maxWidth });
    });
  }, [offset, side]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    const observer = new ResizeObserver(() => updatePosition());
    if (tooltipRef.current) observer.observe(tooltipRef.current);
    if (targetRef.current) observer.observe(targetRef.current);

    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
      observer.disconnect();
    };
  }, [isOpen, updatePosition]);

  const child = React.Children.only(children) as React.ReactElement;
  const childProps = child.props as React.HTMLAttributes<HTMLElement>;
  if (!content || disabled) {
    return child;
  }

  const mergedProps: React.HTMLAttributes<HTMLElement> = {
    ...childProps,
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseEnter?.(event);
      showTooltip(event.currentTarget as HTMLElement);
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      childProps.onMouseLeave?.(event);
      hideTooltip();
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      childProps.onFocus?.(event);
      showTooltip(event.currentTarget as HTMLElement);
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      childProps.onBlur?.(event);
      hideTooltip();
    },
  };

  const style: React.CSSProperties = position
    ? {
        top: position.top,
        left: position.left,
        maxWidth: position.maxWidth,
      }
    : {
        top: 0,
        left: 0,
        visibility: "hidden",
      };

  return (
    <>
      {React.cloneElement(child, mergedProps)}
      {isOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              role="tooltip"
              ref={tooltipRef}
              className={`floating-tooltip ${className ?? ""}`.trim()}
              style={style}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default FloatingTooltip;
